import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { toast } from 'sonner';
import { FileText, ShieldCheck } from 'lucide-react';

export const AuditComplianceSettings: React.FC = () => {
  const [doc, setDoc] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/settings/audit').then(r => setDoc(r.data)); }, []);
  if (!doc) return <div>Loading…</div>;
  const save = async (): Promise<void> => {
    setBusy(true);
    try { await api.put('/settings/audit', doc); toast.success('Saved · password policy updates apply on next create/reset'); }
    catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };
  const num = (k: string) => (
    <div className="space-y-1.5"><Label>{k.replace(/_/g, ' ')}</Label>
      <Input type="number" value={doc[k]} onChange={(e) => setDoc({ ...doc, [k]: parseInt(e.target.value || '0', 10) })} />
    </div>
  );
  const bool = (k: string) => (
    <label className="flex items-center gap-2">
      <input type="checkbox" checked={Boolean(doc[k])} onChange={(e) => setDoc({ ...doc, [k]: e.target.checked })} />
      <span className="text-sm font-body">{k.replace(/_/g, ' ')}</span>
    </label>
  );

  return (
    <div className="space-y-4">
      <Card className="border-raptor-blue/30 bg-raptor-blue/5">
        <CardContent className="p-4 text-sm font-body text-raptor-navy/80 flex items-start gap-3">
          <FileText size={20} className="text-raptor-blue mt-0.5" />
          <div>
            <div className="font-semibold text-raptor-navy">SOC 2 Type II. Audit posture</div>
            <div className="mt-1">
              System audit log records the actor, action, target, ISO 8601 UTC timestamp,
              client IP + user-agent (&quot;where from&quot;), and impersonation flag on every
              privileged action. The API exposes no update or delete endpoints for
              <code> system_audit_log</code>, making it append-only. Retention is the value
              below (default <b>7 years</b>) which satisfies FSCA + SOC 2 CC7.2 / CC7.3
              evidentiary requirements.
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Retention &amp; POPIA</CardTitle><CardDescription>FSCA default: 7-year audit log retention. Configurable.</CardDescription></CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          {num('audit_log_retention_years')}
          {num('csv_export_retention_days')}
          {num('policy_pdf_retention_years')}
          <div className="space-y-1.5"><Label>POPIA DSR inbox</Label><Input value={doc.popia_dsr_inbox} onChange={(e) => setDoc({ ...doc, popia_dsr_inbox: e.target.value })} /></div>
          <div className="md:col-span-2 flex flex-col gap-2">{bool('popia_consent_tracking')}{bool('right_to_erasure_enabled')}{bool('tcf_evidence_capture')}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Session policy</CardTitle><CardDescription>Idle timeout signs users out automatically after the set minutes of no activity.</CardDescription></CardHeader>
        <CardContent className="grid md:grid-cols-4 gap-4">
          {num('access_token_ttl_min')}{num('refresh_token_ttl_days')}{num('idle_timeout_min')}{num('max_concurrent_sessions')}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldCheck size={18} /> Sign-in security</CardTitle>
          <CardDescription>Block sign-in after repeated failed attempts and control two-factor authentication.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2">{bool('lockout_enabled')}</div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Failed logins allowed</Label>
              <Input type="number" min={1} disabled={!doc.lockout_enabled} value={doc.max_failed_logins}
                onChange={(e) => setDoc({ ...doc, max_failed_logins: parseInt(e.target.value || '0', 10) })} data-testid="max-failed-logins" />
              <div className="text-xs text-raptor-navy/50 font-body">After this many failures the account is blocked.</div>
            </div>
            <div className="space-y-1.5">
              <Label>Lockout minutes</Label>
              <Input type="number" min={1} disabled={!doc.lockout_enabled} value={doc.lockout_minutes}
                onChange={(e) => setDoc({ ...doc, lockout_minutes: parseInt(e.target.value || '0', 10) })} data-testid="lockout-minutes" />
              <div className="text-xs text-raptor-navy/50 font-body">How long sign-in stays blocked.</div>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border/60 p-3">
            <div>
              <div className="text-sm font-medium text-raptor-navy">Two-factor authentication (2FA)</div>
              <div className="text-xs font-body mt-0.5">
                Status: <span className={doc.two_factor_enabled ? 'text-green-600 font-semibold' : 'text-raptor-navy/50 font-semibold'} data-testid="twofa-status">
                  {doc.two_factor_enabled ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>
            <label className="flex items-center gap-2" data-testid="twofa-toggle">
              <input type="checkbox" checked={Boolean(doc.two_factor_enabled)}
                onChange={(e) => setDoc({ ...doc, two_factor_enabled: e.target.checked })} />
              <span className="text-sm font-body">Require 2FA</span>
            </label>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Password policy</CardTitle><CardDescription>Enforced on user create + reset + force-reset flows.</CardDescription></CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          {num('password_min_length')}
          {num('password_history_count')}
          {num('password_expiry_days_insurer')}
          {num('password_expiry_days_broker_client')}
          <div className="md:col-span-2 flex flex-col gap-2">{bool('password_require_upper')}{bool('password_require_lower')}{bool('password_require_digit')}{bool('password_require_symbol')}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><FileText size={18} /> Documentation</CardTitle><CardDescription>Every setting + adapter is documented.</CardDescription></CardHeader>
        <CardContent className="text-sm font-body space-y-1">
          <div>· <code className="font-tabular">/app/docs/README.md</code></div>
          <div>· <code className="font-tabular">/app/docs/PROVIDERS.md</code></div>
          <div>· <code className="font-tabular">/app/docs/SETTINGS.md</code></div>
          <div>· <code className="font-tabular">/app/docs/ARCHITECTURE.md</code></div>
        </CardContent>
      </Card>
      <div className="flex justify-end"><Button variant="gradient" disabled={busy} onClick={save}>Save policies</Button></div>
    </div>
  );
};
